/*
 * WW Sigma UseCases — expand/collapse accordion (Swiper carries the scrolling)
 * Author: Tayler — 2026-06-29
 * Version: 2.4.0 - 2026-09-08 10:20 (UTC+8) - Plaud Tayler - prev/next now report the ends: aria-disabled + .is-disabled tracked from Swiper's isBeginning/isEnd, and a disabled arrow no longer acts. Purely additive to the look - the dim is opacity-gated and defaults to none.
 * Version-note: 2.3.0 - 2026-08-25 17:10 (UTC+8) - Plaud Tayler — accordion gate lowered to >=1025 (toggle now live 1025-1439, matching the CSS non-expandable retreat to <=1024)
 * Version-note: 2.2.0 - 2026-08-10 11:45 (UTC+8) - Plaud Tayler — DTC-995: arrows un-gated from the >=1440 accordion guard (dead on mobile/tablet) + swiperOf falls back to el.swiper (homepage LazyLoader path never sets globalSlide)
 * Version-note: 2.1.0 - 2026-08-06 19:55 — accordion gated to >=1440 (below: non-expandable design; taps no longer trigger slideTo jumps)
 * Version-note: 2.0.0 - 2026-08-05 18:55 (UTC+8) - Plaud Tayler — scroll/drag/keyboard migrated to SlideSection/Swiper
 *   (user asked for drag feel benchmarked to Swiper; under slidesPerView:auto an in-flow card width
 *   change can express the accordion). This file keeps only the requirement-specific customization:
 *   the one-open-at-a-time expand accordion (420→856) — after the expand animation ends,
 *   swiper.update() recomputes the snap grid and slideTo moves to the expanded card; arrows now call
 *   swiper.slidePrev/Next (card-by-card sliding; the expanded-state step-size problem dissolves
 *   naturally with the move to the API).
 * Document-delegated, binds once, compatible with Section Rendering API hot-reload.
 */
(function () {
  if (window.__wwSigmaUseCases) return;
  window.__wwSigmaUseCases = true;

  function swiperOf(sec) {
    var el = sec.querySelector('slide-section.ww-sigma-usecases__slider');
    if (!el) return null;
    // DTC-995: the homepage lazy path (LazyLoader.initSlide) constructs Swiper directly and never
    // sets el.globalSlide — fall back to Swiper's own el.swiper handle - Plaud Tayler - 2026-08-10
    if (el.globalSlide && !el.globalSlide.destroyed) return el.globalSlide;
    if (el.swiper && !el.swiper.destroyed) return el.swiper;
    return null;
  }

  function cardsIn(sec) {
    return Array.prototype.slice.call(sec.querySelectorAll('.ww-sigma-usecases__card'));
  }

  function setExpanded(card, open) {
    card.classList.toggle('is-expanded', open);
    var btn = card.querySelector('[data-uc-toggle]');
    if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function expandOnly(sec, target) {
    var cards = cardsIn(sec);
    cards.forEach(function (c) { setExpanded(c, c === target); });
    var sw = swiperOf(sec);
    if (sw && target) {
      /* After the width transition ends, recompute the Swiper snap grid and slide to the expanded
         card; a fallback timer covers the case where the transition is absent (reduced-motion)
         - Plaud Tayler - 2026-08-05 */
      var done = false;
      var settle = function () {
        if (done) return;
        done = true;
        sw.update();
        sw.slideTo(cards.indexOf(target));
      };
      target.addEventListener('transitionend', function h(e) {
        if (e.propertyName !== 'width') return;
        target.removeEventListener('transitionend', h);
        settle();
      });
      setTimeout(settle, 450);
    }
  }

  /* nav end-state - Plaud Tayler - 2026-09-08 10:20 - Begin
     The arrows called slidePrev/slideNext unconditionally and never reported the ends, so at the
     first or last card a press did nothing with no signal. Swiper already knows (isBeginning /
     isEnd); this mirrors that onto the buttons.
     aria-disabled rather than the `disabled` attribute on purpose: a disabled button drops out of
     the tab order, and a carousel control that vanishes under the keyboard mid-interaction is
     worse than one that announces itself as unavailable. The click guard below does the rest.
     The visual is opacity-only and defaults to 1, so every page already using this section renders
     exactly as before until it opts in via --wsuc-nav-off. */
  function syncNav(sec, sw) {
    if (!sw || sw.destroyed) return;
    var btns = sec.querySelectorAll('[data-uc-nav]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      var off = b.getAttribute('data-uc-nav') === 'prev' ? sw.isBeginning : sw.isEnd;
      /* A deck that fits with nothing to scroll disables both, which is the honest state. */
      if (sw.isBeginning && sw.isEnd) off = true;
      b.setAttribute('aria-disabled', off ? 'true' : 'false');
      b.classList.toggle('is-disabled', !!off);
    }
  }

  function bindNav(sec) {
    if (sec.__wwUcNav) return;
    var attach = function () {
      var sw = swiperOf(sec);
      if (!sw) return false;
      sec.__wwUcNav = true;
      ['slideChange', 'transitionEnd', 'resize', 'update', 'observerUpdate'].forEach(function (ev) {
        sw.on(ev, function () { syncNav(sec, sw); });
      });
      syncNav(sec, sw);
      return true;
    };
    if (attach()) return;
    /* Swiper init is lazy on some paths (homepage LazyLoader), and the instance only appears once
       it stamps its own classes on the host - same wait the tab-slider bridge uses. */
    var host = sec.querySelector('slide-section.ww-sigma-usecases__slider');
    if (!host || host.__wwUcNavObs) return;
    var mo = new MutationObserver(function () {
      if (attach()) { mo.disconnect(); host.__wwUcNavObs = null; }
    });
    mo.observe(host, { attributes: true, attributeFilter: ['class'] });
    host.__wwUcNavObs = mo;
  }

  function bindAllNav() {
    var secs = document.querySelectorAll('.ww-sigma-usecases');
    for (var i = 0; i < secs.length; i++) bindNav(secs[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindAllNav);
  else bindAllNav();
  document.addEventListener('shopify:section:load', bindAllNav);
  /* nav end-state - End - Plaud Tayler - 2026-09-08 10:20 */

  /* expansion exists only at >=1025 (below that the design is non-expandable: toggle hidden,
     description always visible) — without this gate a mobile card tap still ran expandOnly and
     slideTo, making the slider jump. Gate lowered 1440 -> 1025 in lockstep with the CSS
     non-expandable block's upper bound - Plaud Tayler - 2026-08-25 17:10 */
  var expandMq = window.matchMedia('(min-width: 1025px)');

  document.addEventListener('click', function (e) {
    // prev / next arrows → Swiper API. DTC-995: this must run at EVERY breakpoint — it sat
    // behind the expandMq accordion gate, so arrows were dead on mobile/tablet even though
    // only the expand accordion is desktop-scoped - Plaud Tayler - 2026-08-10
    var nav = e.target.closest('[data-uc-nav]');
    if (nav) {
      var navSec = nav.closest('.ww-sigma-usecases');
      /* end reached - the button stays focusable and announced, it just does not act */
      if (nav.getAttribute('aria-disabled') === 'true') return;
      var sw = navSec && swiperOf(navSec);
      if (sw) {
        if (nav.getAttribute('data-uc-nav') === 'prev') sw.slidePrev();
        else sw.slideNext();
        syncNav(navSec, sw);
      }
      return;
    }

    if (!expandMq.matches) return; // expand accordion exists only at >=1025

    var card = e.target.closest('.ww-sigma-usecases__card');
    if (!card) return;
    var sec = card.closest('.ww-sigma-usecases');
    if (!sec) return;
    /* Clicks after a Swiper drag are suppressed by its preventClicks; add one more guard against accidental expand after dragging */
    var swGuard = swiperOf(sec);
    if (swGuard && swGuard.animating) return;

    // explicit +/− toggle: collapse if open, otherwise expand (and collapse siblings)
    if (e.target.closest('[data-uc-toggle]')) {
      if (card.classList.contains('is-expanded')) {
        setExpanded(card, false);
        var s2 = swiperOf(sec);
        if (s2) setTimeout(function () { s2.update(); }, 450);
      } else {
        expandOnly(sec, card);
      }
      return;
    }

    // clicking a collapsed card expands it; clicks inside an open card are left alone (readable)
    if (!card.classList.contains('is-expanded')) expandOnly(sec, card);
  });
})();
